<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import FluentIcon from 'shared/components/FluentIcon/Index.vue';

const props = defineProps({
  config: {
    type: Object,
    default: () => ({}),
  },
});

const { formatMessage } = useMessageFormatter();
const { t } = useI18n();

const isDefaultScreen = computed(() => {
  return (
    props.config.isDefaultScreen &&
    ((props.config.welcomeHeading &&
      props.config.welcomeHeading.length !== 0) ||
      (props.config.welcomeTagLine && props.config.welcomeTagline.length !== 0))
  );
});
</script>

<template>
  <div
    class="rounded-t-lg flex-shrink-0 transition-[max-height] duration-300"
    :class="
      isDefaultScreen
        ? 'bg-n-slate-2 dark:bg-n-solid-1 px-4 py-5'
        : 'bg-n-slate-2 dark:bg-n-solid-1 p-4'
    "
  >
    <div class="relative top-px">
      <div class="flex items-start justify-between gap-3">
        <div class="flex items-center min-w-0">
          <img
            v-if="config.logo"
            :src="config.logo"
            class="mr-2 rounded-full logo"
            :class="!isDefaultScreen ? 'h-8 w-8 mb-1' : 'h-12 w-12 mb-2'"
          />
          <div v-if="!isDefaultScreen" class="min-w-0">
            <div class="flex items-center justify-start gap-1">
              <span
                class="text-base font-medium leading-3 truncate text-n-slate-12"
              >
                {{ config.websiteName }}
              </span>
              <div
                v-if="config.isOnline"
                class="w-2 h-2 bg-n-teal-10 rounded-full flex-shrink-0"
              />
            </div>
            <span class="block mt-1 text-xs truncate text-n-slate-11">
              {{ config.replyTime }}
            </span>
          </div>
        </div>
        <div
          v-if="!isDefaultScreen"
          class="flex items-center gap-2 flex-shrink-0"
        >
          <button
            type="button"
            class="inline-flex items-center gap-1 px-1 min-h-7 text-xs font-medium text-n-slate-12"
          >
            <FluentIcon icon="dismiss" size="14" class="text-n-slate-12" />
            <span class="whitespace-nowrap">
              {{ t('INBOX_MGMT.WIDGET_BUILDER.HEADER_ACTIONS.CLOSE_WINDOW') }}
            </span>
          </button>
          <button
            type="button"
            class="inline-flex items-center gap-1 px-1 min-h-7 text-xs font-medium text-n-slate-12"
          >
            <svg
              class="w-3.5 h-3.5 text-n-slate-12"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M8 2.25v5"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
              />
              <path
                d="M5.35 4.3A5 5 0 1 0 10.65 4.3"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
              />
            </svg>
            <span class="whitespace-nowrap">
              {{
                t('INBOX_MGMT.WIDGET_BUILDER.HEADER_ACTIONS.END_CONVERSATION')
              }}
            </span>
          </button>
        </div>
      </div>
      <div v-if="isDefaultScreen" class="overflow-auto max-h-60">
        <h2 class="mb-2 text-2xl break-words text-n-slate-12">
          {{ config.welcomeHeading }}
        </h2>
        <p
          v-dompurify-html="formatMessage(config.welcomeTagline)"
          class="text-sm break-words text-n-slate-11 [&_a]:!text-n-slate-11 [&_a]:underline"
        />
      </div>
    </div>
  </div>
</template>
